/* Pure Sleep Finder recommendation engine. No DOM. Behaviour ported from prototype v0.13. -- SG 20261026 */

export interface FinderWarning {
  code: string;
  vars: Record<string, string>;
}

export interface FinderSlot {
  slot: string;
  group: string;
  reason: string;
  qty: number;
  product_id: string;
  variant_id: string;
  title: string;
  variant_title: string;
  handle: string;
  url: string;
  image: string;
  price: number;
  tog: string;
  colour: string;
  print: string;
  product_type: string;
  inventory: number | null;
  score: number;
}

export interface FinderResult {
  inputs: Record<string, unknown>;
  stage: string;
  stage_name: string;
  tog: string;
  fabric: string;
  temp_band: string;
  size: string;
  kit: FinderSlot[];
  total: number;
  education: string[];
  education_by_group: Record<string, string[]>;
  warnings: FinderWarning[];
  disclaimer: string;
}

export interface FabricOptionsResult {
  ask: boolean;
  options: Array<Record<string, string>>;
  tog: string;
  size: string;
  stage: string;
}

interface EngineState {
  rules: any;
  products: any[];
  ready: boolean;
}

const state: EngineState = { rules: null, products: [], ready: false };

function split(value: unknown): string[] {
  return String(value || "")
    .split("|")
    .map((part) => part.trim())
    .filter(Boolean);
}

function lower(value: unknown): string {
  return String(value || "").toLowerCase();
}

function tagValue(product: any, key: string): string {
  const prefix = key + ":";
  for (const tag of product.tags || []) {
    if (String(tag).indexOf(prefix) === 0) return String(tag).slice(prefix.length).trim();
  }
  return "";
}

function hasTag(product: any, tag: string): boolean {
  return (product.tags || []).indexOf(tag) !== -1;
}

function contains(hay: unknown, needles: unknown): boolean {
  const text = lower(hay);
  return split(needles).some((needle) => text.indexOf(lower(needle)) !== -1);
}

function findBy(list: any[] | null | undefined, key: string, value: unknown): any {
  if (!list) return null;
  for (const row of list) {
    if (row && row[key] === value) return row;
  }
  return null;
}

function warn(list: FinderWarning[], code: string, vars: Record<string, string> = {}): void {
  list.push({ code, vars });
}

function rulesSettings(): any {
  return (state.rules && state.rules.settings) || {};
}

function allTogs(): string[] {
  const list = rulesSettings().all_togs;
  return Array.isArray(list) ? list.map(String) : [];
}

function fallbackTypes(): any {
  return rulesSettings().fallback_product_types || {};
}

function copyOf(key: string): string {
  const copy = (state.rules && state.rules.copy) || {};
  return copy[key] != null ? String(copy[key]) : "";
}

function fillTemplate(template: string, vars: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, (_match, key) => (vars[key] != null ? vars[key] : ""));
}

function joinPhrase(items: string[]): string {
  const parts = items.map((item) => item.trim()).filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0];
  const template = copyOf("list_and");
  const left = parts.slice(0, -1).join(", ");
  const right = parts[parts.length - 1];
  if (!template) return left + ", " + right;
  return fillTemplate(template, { left, right });
}

/* A plural label uses layer_item. A singular label uses layer_item_one. The words live in those templates. -- SG 20261026 */
function layerPhrase(name: string): string {
  const one = copyOf("layer_item_one");
  const plain = copyOf("layer_item");
  const template = one && !/pajamas$|s$/i.test(name) ? one : plain;
  if (!template) return name;
  return fillTemplate(template, { name });
}

/* A missing coming_soon value is the products.json fallback. Treat it as not recommendable. -- SG 20261026 */
function comingSoonOf(product: any): boolean {
  if (product.coming_soon === true || product.coming_soon === "true") return true;
  if (product.coming_soon === false || product.coming_soon === "false") return false;
  return true;
}

/* Coarse stock from the data view. "ok" sits on the threshold so it beats "low" without publishing a count. -- SG 20261026 */
function inventoryOf(variant: any): number | null {
  if (variant.stock === "low") return 0;
  if (variant.stock === "ok") {
    const threshold = Number((state.rules && state.rules.scoring && state.rules.scoring.low_stock_threshold) || 5);
    return threshold;
  }
  if (typeof variant.inventory === "number") return variant.inventory;
  if (typeof variant.inventory_quantity === "number") return variant.inventory_quantity;
  if (typeof variant.inventoryQuantity === "number") return variant.inventoryQuantity;
  return null;
}

export function materialFamily(material: unknown): string {
  const text = lower(material);
  if (!text) return "";
  const families = (state.rules && state.rules.fabrics && state.rules.fabrics.families) || [];
  const ordered = families.slice().sort((left: any, right: any) => (left.order || 99) - (right.order || 99));
  for (const family of ordered) {
    if (family.match && text.indexOf(lower(family.match)) !== -1) return family.id;
  }
  return "";
}

export function ageBandFor(months: number): any {
  const bands = state.rules.age_bands;
  for (const band of bands) {
    if (months >= band.min_months && months < band.max_months) return band;
  }
  if (months < 0) return bands[0];
  return bands[bands.length - 1];
}

export function parseDate(value: string, format: string): string | null {
  const match = String(value || "").match(/^(\d{1,2})[\/.-](\d{1,2})[\/.-](\d{4})$/);
  if (!match) return null;
  const month = format === "DD/MM/YYYY" ? +match[2] : +match[1];
  const day = format === "DD/MM/YYYY" ? +match[1] : +match[2];
  const year = +match[3];
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const date = new Date(year, month - 1, day);
  if (date.getMonth() !== month - 1 || date.getDate() !== day) return null;
  return year + "-" + ("0" + month).slice(-2) + "-" + ("0" + day).slice(-2);
}

export function monthsFromDate(dateStr: string, today?: string): number | null {
  const date = new Date(dateStr + "T00:00:00");
  const now = today ? new Date(today) : new Date();
  if (isNaN(date.getTime())) return null;
  const months =
    (now.getFullYear() - date.getFullYear()) * 12 + (now.getMonth() - date.getMonth()) + (now.getDate() - date.getDate()) / 30;
  return Math.round(months * 10) / 10;
}

export function tempBandFor(tempF: number): any {
  const bands = state.rules.temp_bands;
  for (const band of bands) {
    if (tempF >= band.min_f && tempF < band.max_f) return band;
  }
  return findBy(bands, "id", "comfortable") || bands[0];
}

function cToF(celsius: number): number {
  return (celsius * 9) / 5 + 32;
}

function kgToLb(kg: number): number {
  return kg * 2.20462;
}

function colourGroupFor(product: any): string {
  const colors = state.rules.colors || {};
  const map = colors.filter_map || {};
  const filter = String(product.colour_filter || "").trim();
  if (filter && map[filter]) return map[filter];
  const legacy = (colors.legacy_family_map || {})[product.colour_family];
  return legacy || "other";
}

function isPrint(product: any): boolean {
  const name = String(product.print_name || product.print || "").trim();
  if (!name) return false;
  const colors = state.rules.colors || {};
  const pattern = new RegExp(colors.not_print_pattern || "^plain$|quilt", "i");
  return !pattern.test(name);
}

export function seasonFor(month: number): any {
  const season = state.rules.season_tog;
  if (!season || !season.rows) return null;
  let shifted = ((month - 1) % 12 + 12) % 12 + 1;
  if (state.rules.settings.hemisphere === "north") shifted = ((shifted + 5) % 12) + 1;
  for (const row of season.rows) {
    if (split(row.months_au).map(Number).indexOf(shifted) !== -1) return row;
  }
  return null;
}

function colourPrefs(answers: any): string[] {
  if (Array.isArray(answers.colors)) return answers.colors.filter(Boolean);
  if (typeof answers.colors === "string" && answers.colors) return split(answers.colors);
  const legacy: Record<string, string[]> = {
    girl: ["pink", "neutral"],
    boy: ["blue", "neutral"],
    neutral: ["neutral"],
  };
  return legacy[answers.style] || [];
}

function normalise(product: any): any {
  const variants = (product.variants || []).map((variant: any) => {
    const available =
      typeof variant.available === "boolean"
        ? variant.available
        : typeof variant.availableForSale === "boolean"
          ? variant.availableForSale
          : typeof variant.inventory === "number"
            ? variant.inventory > 0
            : true;
    return {
      id: String(variant.id).replace("gid://shopify/ProductVariant/", ""),
      title: variant.title || "",
      price: typeof variant.price_cents === "number" ? variant.price_cents / 100 : parseFloat(variant.price) || 0,
      available,
      inventory: inventoryOf(variant),
    };
  });
  let image = "";
  if (product.featured_image) image = product.featured_image;
  else if (product.images && product.images.length) {
    image = typeof product.images[0] === "string" ? product.images[0] : product.images[0].src || "";
  }
  return {
    id: String(product.id).replace("gid://shopify/Product/", ""),
    title: product.title || "",
    handle: product.handle || "",
    product_type: product.product_type || product.productType || "",
    tags: Array.isArray(product.tags) ? product.tags : split(String(product.tags || "").replace(/,/g, "|")),
    url: product.url || "/products/" + product.handle,
    image,
    variants,
    colour: "",
    print: "",
    tog: "",
    material: "",
    colour_filter: product.colour_filter || "",
    print_name: product.print_name || "",
    coming_soon: comingSoonOf(product),
  };
}

function enrich(product: any): any {
  product.colour = tagValue(product, "colour");
  product.print = tagValue(product, "print");
  product.tog = tagValue(product, "tog") || tagValue(product, "fabric_grade");
  product.material = tagValue(product, "material");
  const family = findBy(state.rules.colour_families, "colour", product.colour);
  product.colour_family = family ? family.family : "other";
  product.colour_group = colourGroupFor(product);
  product.is_print = isPrint(product);
  product.is_clearance = hasTag(product, state.rules.settings.clearance_tag) || /clearance/i.test(product.title);
  product.is_hidden = hasTag(product, state.rules.settings.hide_tag);
  return product;
}

export function setRules(rules: any): any {
  state.rules = rules;
  return rules;
}

export function setProducts(list: any[]): any[] {
  state.products = list.map(normalise).map(enrich);
  state.ready = true;
  return state.products;
}

export function getProducts(): any[] {
  return state.products;
}

export function sizeForWeight(stageId: string, weightLb: number): string {
  const rows = state.rules.weight_sizes.filter((row: any) => row.stage === stageId);
  for (const row of rows) {
    if (weightLb >= row.min_lb && weightLb < row.max_lb) return row.variant_match;
  }
  if (rows.length && weightLb >= rows[rows.length - 1].max_lb) return rows[rows.length - 1].variant_match;
  return rows.length ? rows[0].variant_match : "";
}

function variantMatching(product: any, sizeMatches: string, requireAvailable: boolean): any {
  const wants = split(sizeMatches);
  for (const want of wants) {
    for (const variant of product.variants) {
      const title = lower(variant.title);
      if (title.indexOf(lower(want)) === 0 || title === lower(want)) {
        if (!requireAvailable || variant.available) return variant;
      }
    }
  }
  return null;
}

function prefsOf(style: any): string[] {
  if (Array.isArray(style)) return style;
  if (style && style !== "surprise") return colourPrefs({ style });
  return [];
}

function styleScore(product: any, style: any): number {
  const scoring = state.rules.scoring;
  let score = 0;
  const prefs = prefsOf(style);
  if (prefs.length) {
    if (prefs.indexOf(product.colour_group) !== -1) score += scoring.colour_family_match;
    if (prefs.indexOf("print") !== -1 && product.is_print) score += scoring.print_match;
  }
  if (product.is_clearance) score += scoring.clearance_penalty;
  return score;
}

function styleFits(product: any, style: any): boolean {
  const prefs = prefsOf(style);
  if (!prefs.length) return true;
  const colours = prefs.filter((item) => item !== "print");
  const colourOk = !colours.length || colours.indexOf(product.colour_group) !== -1;
  const printOk = prefs.indexOf("print") === -1 || product.is_print;
  if (colours.length && prefs.indexOf("print") !== -1) return colours.indexOf(product.colour_group) !== -1 || product.is_print;
  return colourOk && printOk;
}

interface PickQuery {
  types?: string;
  excludeTitle?: string;
  togs?: string[];
  size?: string;
  style?: any;
  preferTypes?: string;
  softPreferTypes?: string;
  excludeHandles?: string[];
  avoidColours?: string[];
  material?: string;
  preferColour?: string;
}

function pick(query: PickQuery): any {
  const scoring = state.rules.scoring;
  const types = split(query.types).map(lower);
  let best: any = null;
  for (const product of state.products) {
    if (product.is_hidden || product.coming_soon) continue;
    if (types.length && types.indexOf(lower(product.product_type)) === -1) continue;
    if (query.excludeTitle && contains(product.title, query.excludeTitle)) continue;
    if (query.excludeHandles && query.excludeHandles.indexOf(product.handle) !== -1) continue;
    if (query.material && materialFamily(product.material) !== query.material) continue;
    let togIndex = -1;
    if (query.togs && query.togs.length) {
      togIndex = query.togs.indexOf(product.tog);
      if (togIndex === -1) continue;
    }
    const variant = query.size
      ? variantMatching(product, query.size, true)
      : product.variants.filter((item: any) => item.available)[0];
    if (!variant) continue;
    let score = styleScore(product, query.style) + (togIndex > 0 ? scoring.tog_fallback_penalty * togIndex : 0);
    if (query.preferTypes && split(query.preferTypes).map(lower).indexOf(lower(product.product_type)) !== -1) {
      score += scoring.type_preference_bonus;
    }
    if (query.softPreferTypes && split(query.softPreferTypes).map(lower).indexOf(lower(product.product_type)) !== -1) {
      score += 1;
    }
    if (variant.inventory !== null && variant.inventory < scoring.low_stock_threshold) score += scoring.low_stock_penalty;
    if (query.preferColour && product.colour === query.preferColour) score += 1;
    if (query.avoidColours && query.avoidColours.indexOf(product.colour) !== -1) score -= 1;
    if (!best || score > best.score || (score === best.score && (variant.inventory || 0) > (best.variant.inventory || 0))) {
      best = { product, variant, score, tog_fallback_index: togIndex };
    }
  }
  return best;
}

export function resolveContext(answers: any): any {
  if (!state.ready) throw new Error("SleepFinder not initialised");
  const rules = state.rules;
  const warnings: FinderWarning[] = [];
  let ageMonths = typeof answers.ageMonths === "number" ? answers.ageMonths : answers.birthDate ? monthsFromDate(answers.birthDate) : null;
  let band = answers.ageBand ? findBy(rules.age_bands, "id", answers.ageBand) : ageBandFor(ageMonths);
  if (!band) {
    warn(warnings, "unknown_age_band", { band: String(answers.ageBand || ""), fallback: rules.age_bands[0].id });
    band = rules.age_bands[0];
  }
  if (ageMonths === null) ageMonths = Math.max(0, band.min_months);
  let tempF = typeof answers.tempF === "number" ? answers.tempF : typeof answers.tempC === "number" ? cToF(answers.tempC) : 70;
  let season = null;
  let seasonMonth = null;
  if (answers.tempMode === "season") {
    seasonMonth = band && band.id === "expecting" && answers.dueMonth ? +answers.dueMonth : new Date().getMonth() + 1;
    season = seasonFor(seasonMonth);
    if (season && typeof season.temp_f === "number") tempF = season.temp_f;
  }
  let weightLb =
    typeof answers.weightLb === "number" ? answers.weightLb : typeof answers.weightKg === "number" ? kgToLb(answers.weightKg) : null;
  const rolling = answers.rolling || "unsure";
  const style = colourPrefs(answers);

  let stageId =
    band.ask_rolling === "yes"
      ? rolling === "yes"
        ? band.stage_if_rolling_yes
        : rolling === "no"
          ? band.stage_if_rolling_no
          : band.stage_if_rolling_unsure
      : band.stage_default;
  if (rolling === "yes" && stageId === "stage1") {
    stageId = "stage2";
    warn(warnings, "rolling_override");
  }
  if (weightLb === null) {
    weightLb = band.default_weight_lb || 15;
    if (band.ask_weight === "yes") warn(warnings, "weight_assumed", { weight: String(weightLb) });
  }
  if (stageId === "stage1" && weightLb >= 19) {
    stageId = "stage2";
    warn(warnings, "over_19lb");
  }
  if (stageId === "stage2_early" && weightLb >= 13) stageId = "stage2";
  let stage = findBy(rules.stages, "id", stageId);

  const tempBand = tempBandFor(tempF);
  let togs = [tempBand.tog_primary, tempBand.tog_fallback_1, tempBand.tog_fallback_2].filter(Boolean);
  let comboTogs: string[] = [];
  if (season) {
    const seasonTogs = split(season.togs);
    if (season.combo === "yes" && seasonTogs.length > 1) comboTogs = seasonTogs;
    togs = seasonTogs.concat(togs.filter((tog: string) => seasonTogs.indexOf(tog) === -1));
  }

  let size = "";
  const sizeBy = stage.size_by;
  const stage3 = findBy(rules.stage3_preference, "age_band", band.id);
  if (sizeBy === "weight") {
    size = stage.force_variant_match || band.force_size || sizeForWeight(stageId === "stage2_early" ? "stage2" : stageId, weightLb);
  } else {
    size = (stage3 && stage3.size_match) || band.stage3_size;
  }

  let preferTypes = "";
  let softPrefer = "";
  if (stageId === "stage3") {
    const sleevePref = findBy(rules.tog_sleeve_preference, "tog", togs[0]);
    preferTypes = stage3 ? stage3.prefer_types : "";
    softPrefer = sleevePref ? sleevePref.prefer_types : "";
  }
  const noTog = stage.ignore_tog === "yes";
  if (noTog) togs = [];
  let heroTypes = stage.hero_product_types;
  if (stageId === "stage3" && rules.preferences && (answers.s3type || answers.sleeves)) {
    const typeOpt = findBy(rules.preferences.type_options, "id", answers.s3type || "");
    const sleeveOpt = findBy(rules.preferences.sleeve_options, "id", answers.sleeves || "");
    let pool = split(typeOpt ? typeOpt.types : stage.hero_product_types);
    if (sleeveOpt && sleeveOpt.match) pool = pool.filter((typeName: string) => contains(typeName, sleeveOpt.match));
    if (pool.length) {
      heroTypes = pool.join("|");
      const sleeveForTog = findBy(rules.tog_sleeve_preference, "tog", togs[0] || "");
      preferTypes = "";
      softPrefer = sleeveForTog ? pool.filter((typeName: string) => contains(sleeveForTog.prefer_types, typeName)).join("|") : "";
    }
  }

  return {
    warnings,
    band,
    ageMonths,
    tempF,
    weightLb,
    rolling,
    style,
    stageId,
    stage,
    togs,
    size,
    sizeBy,
    s3: stage3,
    tb: tempBand,
    noTog,
    heroTypes,
    preferTypes,
    softPrefer,
    season,
    seasonMonth,
    comboTogs: noTog ? [] : comboTogs,
  };
}

export function fabricOptions(answers: any): FabricOptionsResult {
  const context = resolveContext(answers);
  const rules = state.rules;
  const result: FabricOptionsResult = {
    ask: false,
    options: [],
    tog: context.noTog ? "" : context.togs[0] || "",
    size: context.size,
    stage: context.stageId,
  };
  if (!rules.fabrics || context.noTog || !context.togs.length) return result;
  const row = findBy(rules.fabrics.by_tog, "tog", context.togs[0]);
  if (!row || !row.fabrics) return result;
  const types = split(context.heroTypes).map(lower);
  const live = split(row.fabrics).filter((family: string) => {
    return state.products.some((product) => {
      if (product.is_hidden || product.coming_soon) return false;
      if (types.length && types.indexOf(lower(product.product_type)) === -1) return false;
      if (context.stage.exclude_title_contains && contains(product.title, context.stage.exclude_title_contains)) return false;
      if (product.tog !== context.togs[0]) return false;
      if (materialFamily(product.material) !== family) return false;
      return !!variantMatching(product, context.size, true);
    });
  });
  result.options = live.map((id: string) => findBy(rules.fabrics.options, "id", id) || { id, label: id });
  result.ask = result.options.length >= 2;
  return result;
}

function heroKind(productType: string): string {
  const pattern = String(fallbackTypes().hero_kind_pattern || "");
  if (!pattern) return productType.toLowerCase();
  const source = split(pattern)
    .map((part) => part.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
    .join("|");
  const match = new RegExp("(" + source + ")", "i").exec(productType);
  return match ? match[1].toLowerCase() : productType.toLowerCase();
}

function slot(name: string, hit: any, qty: number, reason: string, group: string): FinderSlot {
  return {
    slot: name,
    group: group || name,
    reason,
    qty,
    product_id: hit.product.id,
    variant_id: hit.variant.id,
    title: hit.product.title,
    variant_title: hit.variant.title,
    handle: hit.product.handle,
    url: hit.product.url,
    image: hit.product.image,
    price: hit.variant.price,
    tog: hit.product.tog,
    colour: hit.product.colour,
    print: hit.product.print,
    product_type: hit.product.product_type,
    inventory: hit.variant.inventory,
    score: hit.score,
  };
}

export function recommend(answers: any): FinderResult {
  const context = resolveContext(answers);
  const rules = state.rules;
  const warnings: FinderWarning[] = context.warnings;
  const band = context.band;
  const ageMonths = context.ageMonths;
  const tempF = context.tempF;
  let weightLb = context.weightLb;
  const rolling = context.rolling;
  const style = context.style;
  let stageId = context.stageId;
  let stage = context.stage;
  const togs: string[] = context.togs;
  let size: string = context.size;
  let sizeBy = context.sizeBy;
  const stage3 = context.s3;
  const tempBand = context.tb;
  const noTog = context.noTog;
  let heroTypes = context.heroTypes;
  const preferTypes = context.preferTypes;
  const softPrefer = context.softPrefer;

  const fabricId = answers.fabric || "";
  const fabricOption = fabricId && rules.fabrics ? findBy(rules.fabrics.options, "id", fabricId) : null;
  const heroStyle = fabricOption && fabricOption.style_scope === "layers_only" ? "surprise" : style;
  let heroMaterial = "";
  let hero: any = null;
  if (fabricId && !noTog && togs.length) {
    hero = pick({
      types: heroTypes,
      excludeTitle: stage.exclude_title_contains,
      togs: [togs[0]],
      size,
      style: heroStyle,
      preferTypes,
      softPreferTypes: softPrefer,
      material: fabricId,
    });
    if (hero) heroMaterial = fabricId;
  }
  if (!hero) {
    hero = pick({
      types: heroTypes,
      excludeTitle: stage.exclude_title_contains,
      togs,
      size,
      style: heroStyle,
      preferTypes,
      softPreferTypes: softPrefer,
    });
  }
  if (!hero && heroTypes !== stage.hero_product_types) {
    hero = pick({
      types: stage.hero_product_types,
      excludeTitle: stage.exclude_title_contains,
      togs,
      size,
      style: heroStyle,
    });
    if (hero) warn(warnings, "preferred_style_oos", { size: String(size).split("|")[0] });
  }
  const togList = allTogs();
  if (!hero && sizeBy === "weight" && size === "Small" && rulesSettings().early_roller_any_tog === "yes" && togList.length) {
    hero = pick({
      types: stage.hero_product_types,
      excludeTitle: stage.exclude_title_contains,
      togs: togList,
      size,
      style: heroStyle,
    });
    if (hero) {
      hero.tog_fallback_index = 0;
      warn(warnings, "early_roller_tog", { tog: hero.product.tog });
    }
  }
  if (!hero && stageId === "stage3" && stage3) {
    hero = pick({ types: stage3.then_types, togs, size, style: heroStyle });
  }
  if (!hero) {
    hero = pick({
      types: stage.hero_product_types,
      excludeTitle: stage.exclude_title_contains,
      togs,
      size,
      style: "surprise",
    });
  }
  const transitionSuit = fallbackTypes().transition_suit;
  const sleepBagTypes = fallbackTypes().sleep_bag;
  const sleepBagSize = fallbackTypes().sleep_bag_size;
  if (!hero && (stageId === "stage2" || stageId === "stage2_early") && (transitionSuit || sleepBagTypes)) {
    const alt2 = transitionSuit
      ? pick({ types: transitionSuit, togs, size, style: heroStyle })
      : null;
    if (alt2) {
      hero = alt2;
      warn(warnings, "transition_suit", { size });
    } else if (sleepBagTypes) {
      const stage3Row = findBy(rules.stages, "id", "stage3");
      const alt3 = pick({
        types: sleepBagTypes,
        excludeTitle: stage3Row ? stage3Row.exclude_title_contains : "",
        togs,
        size: sleepBagSize || "",
        style: heroStyle,
      });
      if (alt3) {
        hero = alt3;
        stage = stage3Row;
        stageId = "stage3";
        size = String(sleepBagSize || "").split("|")[0];
        sizeBy = "age";
        warn(warnings, "sleep_bag_fallback");
      }
    }
  }
  if (!hero && sizeBy === "weight" && togList.length) {
    const anyTog = pick({
      types: stage.hero_product_types,
      excludeTitle: stage.exclude_title_contains,
      togs: togList,
      size,
      style: heroStyle,
    });
    if (anyTog) {
      hero = anyTog;
      hero.tog_fallback_index = 0;
      warn(warnings, "fit_over_warmth", { size, tog: togs[0] || "", actual: hero.product.tog });
    }
  }
  if (!hero && sizeBy === "weight") {
    const weightRows = rules.weight_sizes.filter((row: any) => row.stage === (stageId === "stage2_early" ? "stage2" : stageId));
    const current = findBy(weightRows, "variant_match", size) || { min_lb: 0 };
    for (const row of weightRows) {
      if (hero) break;
      if (row.variant_match === size) continue;
      if (row.min_lb < current.min_lb) continue;
      hero = pick({
        types: stage.hero_product_types,
        excludeTitle: stage.exclude_title_contains,
        togs,
        size: row.variant_match,
        style: heroStyle,
      });
      if (hero) {
        warn(warnings, "sized_up", { size, tog: togs[0] || "", next: row.variant_match });
        size = row.variant_match;
      }
    }
  }
  if (fabricId && hero && materialFamily(hero.product.material) !== fabricId) {
    const got = findBy(rules.fabrics.families, "id", materialFamily(hero.product.material));
    warn(warnings, "fabric_fallback", {
      fabric: fabricOption ? fabricOption.label : fabricId,
      size: String(size).split("|")[0],
      tog: togs[0] || "",
      actual: got ? got.label : hero.product.material || "",
    });
  }

  const kit: FinderSlot[] = [];
  const heroQty = rulesSettings().hero_qty || 3;
  const heroLabels = [copyOf("wear"), copyOf("wash"), copyOf("spare")];
  const comboTogs: string[] = context.comboTogs || [];
  const newbornRule = rulesSettings().newborn_split === "yes" && stageId === "stage1" && String(size).split("|")[0] === "Newborn";
  const unitSize = (index: number) => (newbornRule && index === heroQty - 1 ? "Small" : size);
  const unitTogs = (index: number) => (comboTogs.length ? [comboTogs[index % comboTogs.length]] : [hero.product.tog]);
  const unitLabel = (index: number) => {
    let base = heroLabels[index] || "";
    if (comboTogs.length) {
      const combo = copyOf("combo_label_" + (index % comboTogs.length));
      if (combo) base = fillTemplate(combo, { tog: comboTogs[index % comboTogs.length] });
    }
    if (newbornRule && index === heroQty - 1 && copyOf("newborn_small_spare")) base = copyOf("newborn_small_spare");
    return base;
  };

  if (hero) {
    if (hero.tog_fallback_index > 0) {
      warn(warnings, "tog_fallback", {
        tog: togs[0] || "",
        size: String(size).split("|")[0],
        actual: hero.product.tog,
      });
    }
    kit.push(slot("hero", hero, 1, unitLabel(0), "hero"));
    const used = [hero.product.handle];
    let altTypes = hero.product.product_type;
    let altSoft = "";
    const usedColours = [hero.product.colour];
    if (stageId === "stage3") {
      const sleevePref = findBy(rules.tog_sleeve_preference, "tog", hero.product.tog);
      const shape = hero.product.product_type.split(" ").slice(0, 2).join(" ");
      const shapePool = split(heroTypes).filter((typeName: string) => typeName.indexOf(shape) === 0);
      if (shapePool.length) {
        altTypes = shapePool.join("|");
        altSoft = hero.product.product_type + (sleevePref ? "|" + sleevePref.prefer_types : "");
      }
    }
    for (let index = 1; index < heroQty; index++) {
      const nextSize = unitSize(index);
      const nextTogs = unitTogs(index);
      const sizeChange = nextSize !== size;
      let alt = pick({
        types: altTypes,
        excludeTitle: stage.exclude_title_contains,
        togs: nextTogs,
        size: nextSize,
        style: heroStyle,
        excludeHandles: sizeChange ? [] : used,
        softPreferTypes: altSoft,
        avoidColours: usedColours,
        material: heroMaterial,
      });
      if (alt && !styleFits(alt.product, heroStyle)) alt = null;
      if (!alt && comboTogs.length) {
        alt = pick({
          types: altTypes,
          excludeTitle: stage.exclude_title_contains,
          togs: [hero.product.tog],
          size: nextSize,
          style: heroStyle,
          excludeHandles: sizeChange ? [] : used,
          softPreferTypes: altSoft,
          avoidColours: usedColours,
          material: heroMaterial,
        });
        if (alt && !styleFits(alt.product, heroStyle)) alt = null;
      }
      if (!alt && sizeChange) {
        alt = pick({
          types: altTypes,
          excludeTitle: stage.exclude_title_contains,
          togs,
          size: nextSize,
          style: "surprise",
          material: heroMaterial,
        });
      }
      if (alt) {
        if (!sizeChange) used.push(alt.product.handle);
        usedColours.push(alt.product.colour);
        let label = unitLabel(index);
        if (comboTogs.length && alt.product.tog !== nextTogs[0]) label = unitLabel(0);
        kit.push(slot("hero", alt, 1, label, "hero"));
      } else if (kit.length) {
        const last = kit[kit.length - 1];
        last.qty += 1;
        if (heroLabels[index]) last.reason += " + " + heroLabels[index];
        if (sizeChange) warn(warnings, "newborn_small_missing", { size: String(size).split("|")[0] });
      }
    }
  } else {
    warn(warnings, "no_match");
  }

  const togUsed = noTog ? "" : hero ? hero.product.tog : togs[0];
  const bandOrder = rules.temp_bands.map((item: any) => item.id);
  const rowsForTog = noTog ? [] : rules.layering.filter((row: any) => row.tog === togUsed);
  let layerRule = findBy(rowsForTog, "temp_band", tempBand.id);
  if (!layerRule && rowsForTog.length) {
    const tempIndex = bandOrder.indexOf(tempBand.id);
    rowsForTog.sort(
      (left: any, right: any) =>
        Math.abs(bandOrder.indexOf(left.temp_band) - tempIndex) - Math.abs(bandOrder.indexOf(right.temp_band) - tempIndex)
    );
    layerRule = rowsForTog[0];
  }
  const layerNames: string[] = [];
  if (layerRule) {
    const isToddler = ageMonths >= 12;
    const layerDefs = [
      ["layer_1", "layer_1_qty", copyOf("layer_group")],
      ["layer_2", "layer_2_qty", copyOf("layer_2_reason")],
    ];
    for (const definition of layerDefs) {
      const garment = layerRule[definition[0]];
      const qty = parseInt(layerRule[definition[1]], 10) || 0;
      if (!garment || !qty) continue;
      const layerType = findBy(rules.layer_types, "garment", garment);
      const types = layerType ? (isToddler ? layerType.toddler_types : layerType.baby_types) : "";
      if (layerType) layerNames.push(String(layerType.label).toLowerCase());
      if (!types) {
        if (layerType) warn(warnings, "layer_not_ranged", { label: layerType.label || garment });
        continue;
      }
      const usedLayers: string[] = [];
      for (let index = 0; index < qty; index++) {
        let layer = pick({
          types,
          size: band.layer_size,
          style,
          preferColour: hero ? hero.product.colour : "",
          excludeHandles: usedLayers,
        });
        if (layer && !styleFits(layer.product, style)) layer = null;
        if (layer) {
          usedLayers.push(layer.product.handle);
          const spareLabel = copyOf("spare");
          const layerLabel = layerType ? layerType.label : definition[2];
          const reason =
            index === 0 ? layerLabel : spareLabel ? spareLabel + " " + String(layerLabel).toLowerCase() : String(layerLabel);
          kit.push(slot("layer", layer, 1, reason, "layer"));
        } else if (index === 0) {
          const anyLayer = pick({ types, size: band.layer_size, style: "surprise", excludeHandles: usedLayers });
          if (anyLayer) {
            usedLayers.push(anyLayer.product.handle);
            kit.push(slot("layer", anyLayer, 1, layerType ? layerType.label : definition[2], "layer"));
          } else {
            warn(warnings, "layer_oos", {
              label: layerType ? String(layerType.label).toLowerCase() : "",
              size: String(band.layer_size || "").split("|")[0],
            });
          }
        } else if (kit.length && kit[kit.length - 1].slot === "layer") {
          kit[kit.length - 1].qty += 1;
        }
      }
    }
  }

  const kind = hero ? heroKind(hero.product.product_type) : copyOf("hero_fallback");
  let layerText = "";
  if (layerRule && !noTog) {
    const named = layerNames.slice().reverse();
    const joined = joinPhrase(named.map(layerPhrase));
    const template = joined ? copyOf("layer_sentence") : copyOf("layer_sentence_none");
    layerText = fillTemplate(template, {
      temp: String(tempBand.label || "").replace(/^[^:]+:\s*/, ""),
      tog: togUsed || "",
      hero: kind,
      layers: joined,
    });
  }

  const educationByGroup: Record<string, string[]> = {
    hero: stage && stage.education ? [stage.education] : [],
    layer: layerText ? [layerText] : [],
    extra: [],
    next_stage: [],
  };
  if (context.season && copyOf("season_note")) {
    const monthName = ((rules.season_tog && rules.season_tog.month_names) || [])[(context.seasonMonth || 1) - 1] || "";
    const seasonName = context.season["season_" + (rulesSettings().hemisphere || "south")] || "";
    const key = comboTogs.length ? "season_note_combo" : "season_note";
    educationByGroup.hero.push(
      fillTemplate(copyOf(key), {
        month: monthName,
        season: seasonName,
        togs: joinPhrase(split(context.season.togs)),
      })
    );
  }
  if (band.ask_rolling === "yes" && rolling !== "yes" && copyOf("rolling_signs")) {
    educationByGroup.hero.push(copyOf("rolling_signs"));
  }

  const nextSlots: FinderSlot[] = [];
  if (rulesSettings().next_size_up === "yes" && hero && stageId === "stage1" && String(size).split("|")[0] === "Newborn") {
    const kitHandles = kit.filter((item) => item.group === "hero").map((item) => item.handle);
    const smallInKit = kit
      .filter((item) => item.group === "hero" && /^Small/i.test(item.variant_title))
      .map((item) => item.handle);
    let next = pick({
      types: stage.hero_product_types,
      excludeTitle: stage.exclude_title_contains,
      togs: comboTogs.length ? comboTogs : [togUsed],
      size: "Small",
      style,
      excludeHandles: smallInKit.length ? smallInKit : kitHandles,
    });
    if (next && !styleFits(next.product, style)) {
      next = pick({
        types: stage.hero_product_types,
        excludeTitle: stage.exclude_title_contains,
        togs: comboTogs.length ? comboTogs : [togUsed],
        size: "Small",
        style,
        excludeHandles: [],
      });
    }
    const chosen = next;
    if (chosen && kit.some((item) => item.variant_id === chosen.variant.id)) next = null;
    if (next) nextSlots.push(slot("next_stage", next, 1, copyOf("next_size_up"), "next_stage"));
  }
  if (stage && stage.next_stage) {
    const nextStage = findBy(rules.stages, "id", stage.next_stage);
    const nextSize = nextStage.size_by === "weight" ? sizeForWeight(nextStage.id, Math.max(weightLb, 13)) : sleepBagSize || "6-18 months|6-12 months";
    const next = pick({
      types: nextStage.hero_product_types,
      excludeTitle: nextStage.exclude_title_contains,
      togs: [togUsed],
      size: nextSize,
      style,
    });
    if (next && !kit.some((item) => item.variant_id === next.variant.id)) {
      const reason = fillTemplate(copyOf("next_stage_rolling"), { name: nextStage.name });
      nextSlots.push(slot("next_stage", next, 1, reason, "next_stage"));
    }
  }

  const extras = rules.extras
    .filter((extra: any) => extra.stage === stageId)
    .sort((left: any, right: any) => left.priority - right.priority);
  let added = 0;
  for (const extra of extras) {
    if (added >= (rulesSettings().max_extras || 3)) break;
    const hit = pick({ types: extra.product_type, style });
    if (hit) {
      kit.push(slot("extra", hit, 1, extra.label, "extra"));
      added += 1;
    }
  }
  for (const nextSlot of nextSlots) kit.push(nextSlot);

  const education = educationByGroup.hero.concat(educationByGroup.layer);
  const togLabel =
    comboTogs.length && kit.some((item) => item.group === "hero" && item.tog === comboTogs[1])
      ? comboTogs.join(" + ")
      : togUsed || "";

  return {
    inputs: {
      ageMonths,
      ageBand: band.id,
      birthDate: answers.birthDate || null,
      s3type: answers.s3type || null,
      sleeves: answers.sleeves || null,
      fabric: fabricId || null,
      rolling,
      tempF: Math.round(tempF),
      tempMode: answers.tempMode || "temp",
      dueMonth: answers.dueMonth || null,
      weightLb: Math.round(weightLb * 10) / 10,
      style: style.join("|") || "any",
      colors: style,
    },
    stage: stageId,
    stage_name: stage ? stage.name : stageId,
    tog: togLabel,
    fabric: heroMaterial || (hero ? materialFamily(hero.product.material) : ""),
    temp_band: tempBand.id,
    size: String(size || "").split("|")[0],
    kit,
    total: Math.round(kit.reduce((sum, item) => sum + item.price * item.qty, 0) * 100) / 100,
    education,
    education_by_group: educationByGroup,
    warnings,
    disclaimer: copyOf("disclaimer"),
  };
}

export function formatWarning(warning: FinderWarning, copy: Record<string, string>): string {
  const template = copy["warning_" + warning.code] || warning.code;
  return fillTemplate(template, warning.vars);
}

/* Hero and layer types with no buyable product. The loader emits this as finder_config_warning. -- SG 20261026 */
export function zeroCandidateTypes(): string[] {
  if (!state.ready || !state.rules) return [];
  const wanted = new Set<string>();
  for (const stage of state.rules.stages || []) {
    for (const typeName of split(stage.hero_product_types)) wanted.add(typeName);
  }
  for (const layer of state.rules.layer_types || []) {
    for (const typeName of split(layer.baby_types)) wanted.add(typeName);
    for (const typeName of split(layer.toddler_types)) wanted.add(typeName);
  }
  const missing: string[] = [];
  wanted.forEach((typeName) => {
    const found = state.products.some(
      (product) => !product.is_hidden && !product.coming_soon && lower(product.product_type) === lower(typeName)
    );
    if (!found) missing.push(typeName);
  });
  return missing;
}
